import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target, Plus, Search, Trash2, X, CheckCircle, AlertCircle, Edit2, Users
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  getOrCreatePipeline, getStages, getLeads, createLead, updateLeadStage,
  updateLead, deleteLead, addStage, updateStage, deleteStage,
  type CrmPipeline, type CrmStage, type CrmLead,
} from '../../lib/crm';

type ModalMode = 'add-lead' | 'edit-lead' | 'add-stage' | 'edit-stage' | null;

export function SalesCRM() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [pipeline, setPipeline] = useState<CrmPipeline | null>(null);
  const [stages, setStages] = useState<CrmStage[]>([]);
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingLead, setEditingLead] = useState<CrmLead | null>(null);
  const [editingStage, setEditingStage] = useState<CrmStage | null>(null);
  const [draggedLead, setDraggedLead] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [leadForm, setLeadForm] = useState({
    full_name: '', email: '', phone: '', source: 'manual', notes: '', stage_id: '',
  });
  const [stageForm, setStageForm] = useState({ name: '', color: '#3b82f6' });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) { navigate('/login'); return; }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id, crm_enabled')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) { navigate('/login'); return; }
        setCoachId(coach.id);

        const p = await getOrCreatePipeline(coach.id);
        if (!p) { setError('Failed to load pipeline'); return; }
        setPipeline(p);

        const [s, l] = await Promise.all([getStages(p.id), getLeads(coach.id, p.id)]);
        setStages(s);
        setLeads(l);
      } catch (err) {
        console.error(err);
        setError('Failed to initialize CRM');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const reload = useCallback(async () => {
    if (!coachId || !pipeline) return;
    const [s, l] = await Promise.all([getStages(pipeline.id), getLeads(coachId, pipeline.id)]);
    setStages(s);
    setLeads(l);
  }, [coachId, pipeline]);

  const handleDragStart = (leadId: string) => setDraggedLead(leadId);
  const handleDragEnd = () => { setDraggedLead(null); setDragOverStage(null); };

  const handleDrop = async (stageId: string) => {
    if (!draggedLead) return;
    const lead = leads.find(l => l.id === draggedLead);
    if (!lead || lead.stage_id === stageId) { handleDragEnd(); return; }

    setLeads(prev => prev.map(l => l.id === draggedLead ? { ...l, stage_id: stageId } : l));
    handleDragEnd();
    await updateLeadStage(draggedLead, stageId);
  };

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coachId || !pipeline) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: err } = await createLead({
        coach_id: coachId,
        pipeline_id: pipeline.id,
        stage_id: leadForm.stage_id || stages[0]?.id || '',
        full_name: leadForm.full_name,
        email: leadForm.email,
        phone: leadForm.phone || undefined,
        source: leadForm.source,
        notes: leadForm.notes || undefined,
      });
      if (err) throw err;
      await reload();
      setModalMode(null);
      resetLeadForm();
    } catch (err: any) {
      setError(err.message || 'Failed to add lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLead) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: err } = await updateLead(editingLead.id, {
        full_name: leadForm.full_name,
        email: leadForm.email,
        phone: leadForm.phone || null,
        notes: leadForm.notes || null,
        stage_id: leadForm.stage_id,
      });
      if (err) throw err;
      await reload();
      setModalMode(null);
      resetLeadForm();
    } catch (err: any) {
      setError(err.message || 'Failed to update lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLead = async (id: string) => {
    if (!confirm('Delete this lead?')) return;
    await deleteLead(id);
    await reload();
  };

  const handleAddStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pipeline) return;
    setIsSubmitting(true);
    try {
      await addStage(pipeline.id, stageForm.name, stages.length, stageForm.color);
      await reload();
      setModalMode(null);
      setStageForm({ name: '', color: '#3b82f6' });
    } catch (err: any) {
      setError(err.message || 'Failed to add stage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStage) return;
    setIsSubmitting(true);
    try {
      await updateStage(editingStage.id, { name: stageForm.name, color: stageForm.color });
      await reload();
      setModalMode(null);
      setStageForm({ name: '', color: '#3b82f6' });
    } catch (err: any) {
      setError(err.message || 'Failed to update stage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStage = async (stageId: string) => {
    const stageLeads = leads.filter(l => l.stage_id === stageId);
    if (stageLeads.length > 0) {
      setError('Cannot delete a stage that has leads. Move or delete them first.');
      return;
    }
    if (!confirm('Delete this stage?')) return;
    await deleteStage(stageId);
    await reload();
  };

  const openEditLead = (lead: CrmLead) => {
    setEditingLead(lead);
    setLeadForm({
      full_name: lead.full_name,
      email: lead.email,
      phone: lead.phone || '',
      source: lead.source,
      notes: lead.notes || '',
      stage_id: lead.stage_id,
    });
    setModalMode('edit-lead');
  };

  const openEditStage = (stage: CrmStage) => {
    setEditingStage(stage);
    setStageForm({ name: stage.name, color: stage.color });
    setModalMode('edit-stage');
  };

  const resetLeadForm = () => {
    setLeadForm({ full_name: '', email: '', phone: '', source: 'manual', notes: '', stage_id: '' });
    setEditingLead(null);
  };

  const filteredLeads = leads.filter(l =>
    l.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'assessment_completion': return 'Assessment';
      case 'ai_coach_trial': return 'AI Coach Trial';
      case 'manual': return 'Manual';
      default: return source;
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'assessment_completion': return 'bg-blue-100 text-blue-700';
      case 'ai_coach_trial': return 'bg-teal-100 text-teal-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading CRM..." />;
  }

  return (
    <div className="min-h-screen freud-bg flex flex-col">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-500 rounded-xl shadow-soft">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">Sales Pipeline</h1>
                <p className="freud-text-sm text-chocolate-500">
                  {leads.length} leads across {stages.length} stages
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chocolate-400" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="freud-input pl-9 py-2 text-sm w-48"
                />
              </div>
              <button
                onClick={() => { setModalMode('add-stage'); setStageForm({ name: '', color: '#3b82f6' }); }}
                className="freud-btn-secondary flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Stage
              </button>
              <button
                onClick={() => { setModalMode('add-lead'); resetLeadForm(); }}
                className="freud-btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Lead
              </button>
            </div>
          </div>
        </div>
      </header>

      {error && (
        <div className="mx-4 mt-4 bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      <main className="flex-1 overflow-x-auto p-4">
        <div className="flex gap-4 min-w-max h-full">
          {stages.map(stage => {
            const stageLeads = filteredLeads.filter(l => l.stage_id === stage.id);
            return (
              <div
                key={stage.id}
                className={`w-72 flex-shrink-0 flex flex-col rounded-xl border transition-colors ${
                  dragOverStage === stage.id ? 'border-blue-400 bg-blue-50/50' : 'border-cream-300 bg-white/60'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.id); }}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={() => handleDrop(stage.id)}
              >
                <div className="p-3 border-b border-cream-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="text-sm font-semibold text-chocolate-800">{stage.name}</span>
                    <span className="text-xs text-chocolate-400 bg-cream-200 px-1.5 py-0.5 rounded-full">
                      {stageLeads.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditStage(stage)}
                      className="p-1 text-chocolate-400 hover:text-chocolate-600 rounded"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteStage(stage.id)}
                      className="p-1 text-chocolate-400 hover:text-coral-600 rounded"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-2 space-y-2 max-h-[calc(100vh-220px)]">
                  {stageLeads.map(lead => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={() => handleDragStart(lead.id)}
                      onDragEnd={handleDragEnd}
                      className={`p-3 bg-white rounded-lg border border-cream-200 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group ${
                        draggedLead === lead.id ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <p className="text-sm font-medium text-chocolate-800 truncate flex-1">
                          {lead.full_name}
                        </p>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openEditLead(lead)} className="p-0.5 text-chocolate-400 hover:text-chocolate-600">
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button onClick={() => handleDeleteLead(lead.id)} className="p-0.5 text-chocolate-400 hover:text-coral-600">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-chocolate-500 truncate">{lead.email}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${getSourceColor(lead.source)}`}>
                          {getSourceLabel(lead.source)}
                        </span>
                        <span className="text-xs text-chocolate-400">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {lead.notes && (
                        <p className="text-xs text-chocolate-400 mt-1.5 line-clamp-2">{lead.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Lead Modal */}
      <AnimatePresence>
        {(modalMode === 'add-lead' || modalMode === 'edit-lead') && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="freud-card-elevated p-6 w-full max-w-md m-4"
            >
              <h2 className="text-xl font-bold text-chocolate-900 mb-6">
                {modalMode === 'edit-lead' ? 'Edit Lead' : 'Add New Lead'}
              </h2>
              <form onSubmit={modalMode === 'edit-lead' ? handleEditLead : handleAddLead} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={leadForm.full_name}
                    onChange={(e) => setLeadForm({ ...leadForm, full_name: e.target.value })}
                    required
                    className="freud-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    required
                    className="freud-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                    className="freud-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Stage</label>
                  <select
                    value={leadForm.stage_id}
                    onChange={(e) => setLeadForm({ ...leadForm, stage_id: e.target.value })}
                    className="freud-input"
                  >
                    {stages.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Source</label>
                  <select
                    value={leadForm.source}
                    onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })}
                    className="freud-input"
                  >
                    <option value="manual">Manual</option>
                    <option value="assessment_completion">Assessment Completion</option>
                    <option value="ai_coach_trial">AI Coach Trial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Notes</label>
                  <textarea
                    value={leadForm.notes}
                    onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })}
                    rows={3}
                    className="freud-input resize-none"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setModalMode(null); resetLeadForm(); }} className="freud-btn-secondary flex-1" disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="freud-btn-primary flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : (modalMode === 'edit-lead' ? 'Save' : 'Add Lead')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Stage Modal */}
      <AnimatePresence>
        {(modalMode === 'add-stage' || modalMode === 'edit-stage') && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="freud-card-elevated p-6 w-full max-w-md m-4"
            >
              <h2 className="text-xl font-bold text-chocolate-900 mb-6">
                {modalMode === 'edit-stage' ? 'Edit Stage' : 'Add Stage'}
              </h2>
              <form onSubmit={modalMode === 'edit-stage' ? handleEditStage : handleAddStage} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Stage Name</label>
                  <input
                    type="text"
                    value={stageForm.name}
                    onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
                    required
                    className="freud-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={stageForm.color}
                      onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
                      className="w-10 h-10 rounded-lg border border-cream-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={stageForm.color}
                      onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
                      className="freud-input flex-1"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setModalMode(null)} className="freud-btn-secondary flex-1" disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="freud-btn-primary flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : (modalMode === 'edit-stage' ? 'Save' : 'Add Stage')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
